import React, { useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Modal,
  Stack,
  TextField,
} from '@mui/material'
import { observer } from "mobx-react"
import { useStores } from '../hooks/useStores'
import OrganizationAPI  from '../utilities/OrganizationsHelper'

import { CancelAndSaveButtons } from '../components/reuseable/CancelAndSaveButtons'

const CreateOrganization = observer(() => {
  const { organizationStore, permissionsStore } = useStores()
  const [organizationName, setOrganizationName] = useState('')
  const [authType, setAuthType] = useState('')
  const [ssoDomainName, setSsoDomainName] = useState('')
  const [publicApiAccess, setPublicApiAccess] = useState(false)
  const [authMethod, setAuthMethod] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [errors, setErrors] = useState({});
  const [createdOrganization, setCreatedOrganization] = useState(null);
  const [errorMessage, setErrorMessage] = useState('')
  // Validate required fields
  const validateAll = () => {
    const errors = {};
  
    if (!organizationName?.trim()) errors.organizationName = 'Organization name is required';
  
    if (!authType) errors.authType = 'Select an auth type';
  
    if (authType === 'FEDERATED') {
      if (!ssoDomainName?.trim()) {
        errors.ssoDomainName = 'SSO Domain Name is required for FEDERATED';
      } else if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(ssoDomainName.trim())) {
        errors.ssoDomainName = 'Enter a valid domain (e.g. example.com)';
      }
    }
  
    if (publicApiAccess && !authMethod) {
      errors.authMethod = 'Authentication method is required when Public API access is enabled';
    }
  
    return errors;
  };

  const handleSaveClick = () => {
    const errors = validateAll();
    setErrors(errors);
    if (Object.keys(errors).length === 0) setShowModal(true); // only open modal if valid
  };
  
  const clearError = (key) => setErrors((prev) => ({ ...prev, [key]: undefined }));

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false)
    setCreatedOrganization(null)
    // Reset form after successful creation
    setOrganizationName('')
    setAuthType('')
    setSsoDomainName('')
    setPublicApiAccess(false)
    setAuthMethod('')
    setErrors({})
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false)
    setErrorMessage('')
  }

  const handleCreateOrganization = async () => {
    setIsCreating(true)
    try {
      const orgData = {
        organizationName,
        identityProvider: authType,
        ...(authType === 'FEDERATED' && ssoDomainName && { domainName: ssoDomainName }),
        ...(publicApiAccess && authMethod && { 
          integration: {
            authentication: authMethod
          }
        })
      }

      // Check if user's email is Marley domain
      const userEmail = permissionsStore.user?.email;
      if (userEmail) {
        const userDomain = OrganizationAPI.getNormalizedDomainMarleyUser(userEmail);          
        if (userDomain !== 'himarley.com') {
          console.log(`Cannot create an organization as a non-marley user. Your domain is: ${userDomain}`)
          return;
        }
      }

      const createdOrg = await organizationStore.createOrganization(orgData, permissionsStore.user)
      
      setCreatedOrganization(createdOrg)
      setShowModal(false)
      setShowSuccessModal(true)
      
      console.log('Organization created successfully!')
      
    } catch (error) {
      console.error('Failed to create organization:', error)
      const errorMessage = error.message || 'Failed to create organization'

      setErrorMessage(errorMessage)
      setShowErrorModal(true)
      setShowModal(false)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div className='create-organization-container'>
        <h1 className='title' style={{ width: '100%', maxWidth: '1200px', margin: '20px auto', textAlign: 'center' }}> Create Organization </h1>
        <div className='create-organization-form' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box
                component='form'
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'space-between',
                    width: '500px',
                    height: '500px',
                    textAlign: 'center',
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'space-between' }}>
                    <TextField
                    required
                    label='Organization Name'
                    value={organizationName}
                    onChange={(e) => { setOrganizationName(e.target.value); clearError('organizationName'); }}
                    onBlur={() => setErrors((p) => ({ ...p, ...validateAll() }))}
                    error={!!errors.organizationName}
                    helperText={errors.organizationName}
                    className='org-name'
                    />
                    <Autocomplete 
                        required
                        renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select auth type"
                              error={!!errors.authType}
                              helperText={errors.authType}
                            />
                          )}
                          options={['MARLEY', 'FEDERATED']}
                          value={authType || null}
                          onChange={(_, value) => { setAuthType(value || ''); clearError('authType'); }}
                    />
                    {authType === 'FEDERATED' && (
                        <TextField
                        label='SSO Domain Name'
                        value={ssoDomainName}
                        onChange={(e) => { setSsoDomainName(e.target.value); clearError('ssoDomainName'); }}
                        onBlur={() => setErrors((p) => ({ ...p, ...validateAll() }))}
                        error={!!errors.ssoDomainName}
                        helperText={errors.ssoDomainName}
                        />
                    )}
                    <FormControlLabel
                        control={<Checkbox checked={publicApiAccess} onChange={() => { setPublicApiAccess(!publicApiAccess); clearError('publicApiAccess') }} />}
                        label='Enable Public API access'
                    />
                    {publicApiAccess && (
                        <Autocomplete 
                        required
                        renderInput={(params) => (
                            <TextField {...params} label="Authentication method (KEY or OAUTH2)"
                            error={!!errors.authMethod}
                            helperText={errors.authMethod}
                            />
                        )}
                        options={['OAUTH2', 'KEY']}
                        onChange={(_, value) => { setAuthMethod(value || ''); clearError('authMethod'); }}
                        value={authMethod || null}
                    />
                    )}
                    <CancelAndSaveButtons 
                        disabled={!organizationName || !authType || (publicApiAccess && !authMethod)}
                        cancel={() => setShowModal(false)} 
                        save={handleSaveClick}
                        label={showModal ? 'CREATE' : null}
                    />
                </div>
                {showModal && (
                <Modal 
                    aria-labelledby="simple-modal-title"
                    aria-describedby="simple-modal-description"
                    open={showModal} 
                    onClose={() => setShowModal(false)} 
                    disableAutoFocus={true}
                    disableEnforceFocus={true}
                >
                    <Box
                        sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 700,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                        }}
                    >
                        <h2 style={{ textAlign: 'center' }}>Are you sure you want to create this organization?</h2>
                        <h4 style={{ textAlign: 'center' }}>This action cannot be undone.</h4>
                        <h3>Organization Name: {organizationName}</h3>
                        <h3>Auth Type: {authType}</h3>
                        <h3>SSO Domain Name: {ssoDomainName || 'N/A'}</h3>
                        <h3>Enable Public API access: {publicApiAccess ? 'Yes' : 'No'}</h3>
                        <h3>Authentication method: {authMethod || 'N/A'}</h3>
                        <div className="button_container" style={{ paddingTop: '20px', display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                            <Stack direction="row" spacing={2}>
                            <Button variant="contained" color="secondary" 
                            onClick={() => setShowModal(false)}> Cancel </Button> 
                            <Button variant="contained" color="inherit"
                            onClick={handleCreateOrganization}
                            disabled={isCreating}
                            > {isCreating ? 'Creating...' : 'Confirm'} </Button>
                            </Stack>
                             
                        </div>
                    </Box>
                </Modal>
            )}
            {showSuccessModal && createdOrganization && (
                <Modal 
                    aria-labelledby="success-modal-title"
                    aria-describedby="success-modal-description"
                    open={showSuccessModal} 
                    onClose={handleCloseSuccessModal} 
                    disableAutoFocus={true}
                    disableEnforceFocus={true}
                >
                    <Box
                        role="dialog"
                        aria-modal="true"
                        sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 600,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                        }}
                    >
                        <h2 style={{ textAlign: 'center', color: 'green' }}>Organization Created Successfully!</h2>
                        <h4 style={{ textAlign: 'center' }}>⚠️ WARNING ⚠️<br />All credentials shown below are not stored and need to be moved into 1Password immediately.</h4>
                        <div style={{ marginTop: '20px' }}>
                            <h2>Organization Details:</h2>
                            <div style={{ textAlign: 'left', marginLeft: '20px' }}>
                                <p><strong>Organization Name:</strong> {createdOrganization?.name}</p>
                                <p><strong>Organization ID:</strong> {createdOrganization?._id}</p>
                                <p><strong>Auth Type:</strong> {createdOrganization?.identityProvider}</p>
                                {createdOrganization?.createdAt && (
                                  <p><strong>Created At:</strong> {new Date(createdOrganization.createdAt).toLocaleString()}</p>
                                )}
                                {createdOrganization?.domainName && 
                                 createdOrganization?.domainName?.length > 0 && 
                                 createdOrganization.domainName.some(domain => domain && domain.trim() !== '') && (
                                    <p><strong>Domain Name:</strong> {createdOrganization.domainName.filter(domain => domain && domain.trim() !== '').join(', ')}</p>
                                )}
                                </div>
                                <h2>SMS:</h2>
                                <div style={{ textAlign: 'left', marginLeft: '20px' }}>
                                {createdOrganization?.sms && (
                                  <>
                                    <p><strong>Provider:</strong> {createdOrganization.sms?.provider}</p>
                                    <p><strong>Country Code:</strong> {createdOrganization.sms?.countryCode}</p>
                                    <p><strong>Pool Size:</strong> {createdOrganization.sms?.poolSize}</p>
                                    {createdOrganization?.sms?.numberType && (
                                      <p><strong>Number Type:</strong> {createdOrganization.sms?.numberType}</p>
                                    )}
                                  </>
                                
                                )}
                                </div>
                                <h2>API Integration:</h2>
                                <div style={{ textAlign: 'left', marginLeft: '20px' }}>
                                {createdOrganization?.integration && (
                                  <>
                                    <p><strong>API Authentication:</strong> {createdOrganization.integration?.authentication}</p>
                                    <p><strong>API Key:</strong> {createdOrganization.integration?.apiKey}</p>
                                  </>
                                    
                                )}
                                {createdOrganization?.integration?.authentication === 'OAUTH2' && (
                                  <>
                                    <p><strong>Application ID:</strong> {createdOrganization.integration?.applicationId}</p>
                                    <p><strong>Client ID:</strong> {createdOrganization.integration?.clientId}</p>
                                    <p><strong>Client Secret:</strong> {createdOrganization.integration?.clientSecret}</p>
                                  </>
                                )}
                                </div>
                            </div>
                        <div className="button_container" style={{ paddingTop: '20px', display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                            <Button variant="contained" color="primary" 
                            onClick={handleCloseSuccessModal}>Close</Button>
                        </div>
                    </Box>
                </Modal>
            )}
            {showErrorModal && (
                <Modal 
                    aria-labelledby="error-modal-title"
                    aria-describedby="error-modal-description"
                    open={showErrorModal} 
                    onClose={handleCloseErrorModal} 
                    disableAutoFocus={true}
                    disableEnforceFocus={true}
                >
                    <Box
                        role="dialog"
                        aria-modal="true"
                        sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 600,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                        }}
                    >
                        <h2 style={{ textAlign: 'center', color: 'red' }}>Error Creating Organization</h2>
                        <h4 style={{ textAlign: 'center' }}>{errorMessage}</h4>
                        <div className="button_container" style={{ paddingTop: '20px', display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                            <Button variant="contained" color="primary" 
                            onClick={handleCloseErrorModal}>Close</Button>
                        </div>
                    </Box>
                </Modal>
            )}
            </Box>
        </div>
    </div>
  )
})

export default CreateOrganization